import { useState, useEffect } from "react";
import { useToast } from "@/shared/hooks/use-toast";

interface ImageUploadOptions {
  initialImage?: string | null;
  maxSize?: number;
  bucket?: string;
  folderPath?: string;
}

export const useImageUpload = ({
  initialImage = null,
  maxSize = 300,
}: ImageUploadOptions = {}) => {
  const { toast } = useToast();
  const [imagePreview, setImagePreview] = useState<string | null>(initialImage);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageError, setImageError] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  // Initialize preview from initial data
  useEffect(() => {
    if (initialImage) {
      console.log("Setting image preview from initial data:", initialImage);
      setImagePreview(initialImage);
    }
  }, [initialImage]);

  // Handle image upload
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > maxSize * 1024) {
      toast({
        title: "Erro ao carregar imagem",
        description: `A imagem deve ter no máximo ${maxSize}KB`,
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }

    // Clear previous preview to force update
    setImagePreview(null);
    
    // Use FileReader para lidar com o preview da imagem
    const reader = new FileReader();
    
    reader.onloadend = () => {
      // O resultado será uma string base64
      setImagePreview(reader.result as string);
      setImageFile(file);
      setImageError(false);
    };
    
    // Ler o arquivo como URL de dados (base64)
    reader.readAsDataURL(file);
    
    // Não precisamos mais do cleanup com URL.revokeObjectURL
    return undefined;
  };

  return {
    imagePreview,
    imageFile,
    imageError,
    isUploading,
    setImagePreview,
    setImageFile,
    setImageError,
    handleImageChange,
  };
};
